'use client';

import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Code,
  Copy,
  Download,
  Edit2,
  Eye,
  FileText,
  Globe,
  Link as LinkIcon,
  Loader2,
  Monitor,
  Plus,
  RefreshCw,
  Save,
  Send,
  Settings,
  Smartphone,
  Trash2,
  X,
} from 'lucide-react';

import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';

// --- TYPES ---
type Story = {
  id: string;
  url: string;
  title: string;
  image?: string;
  category?: string;
  dek?: string;
};

type BucketKey = 'top_stories' | 'featured' | 'trending' | 'editors_pick' | 'below_weather';

type BucketConfig = {
  id: BucketKey;
  label: string;
  limit: number;
  description: string;
  fields: ('image' | 'category' | 'dek')[];
};

type PodcastInfo = {
  imageUrl: string;
  url: string;
  title: string;
  body: string;
};

// ✅ Menu: News | Dallas | Podcast | State | Crime
type MenuLinks = {
  news: string;
  dallas: string;
  podcast: string;
  state: string;
  crime: string;
};

const N8N_UPLOAD_WEBHOOK_URL = 'https://n8n.sociallydaily.com/webhook/listmonk-upload-image';

const BUCKETS: BucketConfig[] = [
  {
    id: 'top_stories',
    label: 'Stories',
    limit: 5,
    description: 'Select 5 stories for the newsletter layout',
    fields: ['image', 'category', 'dek'],
  },
  {
    id: 'below_weather',
    label: 'Must Read',
    limit: 1,
    description: 'One featured article below the weather widget',
    fields: ['dek'],
  },
];

// ✅ UTM
const UTM_SOURCE = 'newsletter';
const UTM_MEDIUM = 'email';

const normalizeUtmSpaces = (url: string) => url.replace(/\+/g, '%20');

const withUtm = (rawUrl: string, utmCampaign: string, _utmContent?: string) => {
  try {
    if (!rawUrl || !/^https?:\/\//i.test(rawUrl)) return rawUrl;
    const u = new URL(rawUrl);
    const p = u.searchParams;

    if (!p.has('utm_source')) p.set('utm_source', UTM_SOURCE);
    if (!p.has('utm_medium')) p.set('utm_medium', UTM_MEDIUM);
    if (!p.has('utm_campaign')) p.set('utm_campaign', utmCampaign || 'dx_daily');

    return normalizeUtmSpaces(u.toString());
  } catch {
    const sep = rawUrl.includes('?') ? '&' : '?';
    const out =
      `${rawUrl}${sep}` +
      `utm_source=${encodeURIComponent(UTM_SOURCE)}` +
      `&utm_medium=${encodeURIComponent(UTM_MEDIUM)}` +
      `&utm_campaign=${encodeURIComponent(utmCampaign || 'dx_daily')}`;
    return out;
  }
};

// ✅ Parse multiple list IDs ("1,2,7")
const parseListIds = (input: string): number[] => {
  const nums = (input || '')
    .split(/[\s,]+/)
    .map(s => parseInt(s.trim(), 10))
    .filter(n => Number.isFinite(n) && n > 0);

  return Array.from(new Set(nums));
};

const normalizeListmonkMediaUrl = (base: string, media: any) => {
  const raw = media?.url || media?.uri || '';
  if (!raw) return '';

  return /^https?:\/\//i.test(raw)
    ? raw
    : `${base}${raw.startsWith('/') ? '' : '/'}${raw}`;
};

const fileNameFromUrl = (rawUrl: string) => {
  try {
    const u = new URL(rawUrl);
    return decodeURIComponent(u.pathname.split('/').pop() || 'image.jpg');
  } catch {
    return 'image.jpg';
  }
};

const uploadImageToListmonk = async (
  imageUrl: string,
  base: string,
  auth: string
) => {
  try {
    console.log('STEP 1: downloading image', imageUrl);

    let sourceRes: Response;
    try {
      sourceRes = await fetch(imageUrl, {
        cache: 'no-store',
      });
    } catch (err: any) {
      throw new Error(
        `STEP 1 FETCH FAILED: browser could not download image from ${imageUrl}. ${err?.message || err || ''}`
      );
    }

    console.log('STEP 1 OK', sourceRes.status, sourceRes.statusText);

    if (!sourceRes.ok) {
      const txt = await sourceRes.text().catch(() => '');
      throw new Error(
        `STEP 1 HTTP FAILED: image download returned ${sourceRes.status} ${sourceRes.statusText}. ${txt}`
      );
    }

    const blob = await sourceRes.blob();
    console.log('STEP 2: got blob', { size: blob.size, type: blob.type });

    const form = new FormData();
    form.append('file', blob, fileNameFromUrl(imageUrl));

    console.log('STEP 3: uploading to listmonk', `${base}/api/media`);

    let uploadRes: Response;
    try {
      uploadRes = await fetch(`${base}/api/media`, {
        method: 'POST',
        headers: {
          Authorization: `Basic ${auth}`,
        },
        body: form,
      });
    } catch (err: any) {
      throw new Error(
        `STEP 3 FETCH FAILED: browser could not upload image to ${base}/api/media. ${err?.message || err || ''}`
      );
    }

    console.log('STEP 3 RESPONSE', uploadRes.status, uploadRes.statusText);

    const rawText = await uploadRes.text();
    console.log('STEP 3 BODY', rawText);

    if (!uploadRes.ok) {
      throw new Error(
        `STEP 3 HTTP FAILED: upload returned ${uploadRes.status} ${uploadRes.statusText}. ${rawText}`
      );
    }

    let uploadData: any = {};
    try {
      uploadData = rawText ? JSON.parse(rawText) : {};
    } catch {
      throw new Error(`STEP 3 PARSE FAILED: upload response was not valid JSON. ${rawText}`);
    }

    const uploadedUrl =
      uploadData?.data?.url ||
      (uploadData?.data?.uri
        ? `${base}${String(uploadData.data.uri).startsWith('/') ? '' : '/'}${uploadData.data.uri}`
        : '');

    console.log('STEP 3 PARSED URL', uploadedUrl);

    if (!uploadedUrl) {
      throw new Error(
        `STEP 3 FAILED: upload succeeded but no data.url or data.uri found. Response: ${rawText}`
      );
    }

    return uploadedUrl;
  } catch (e) {
    console.error('FAILED HERE:', e);
    throw e;
  }
};

// --- HELPER: DECODE HTML ENTITIES ---
const decodeHtml = (html: string) => {
  if (typeof document === 'undefined') return html;
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
};

// --- HELPER: FETCH FULL CONTENT (FIXED FOR UTMs / ?query) ---
const getSlugFromUrl = (rawUrl: string) => {
  try {
    const u = new URL(rawUrl);
    const parts = u.pathname.split('/').filter(Boolean);
    return parts.length ? parts[parts.length - 1] : null;
  } catch {
    const cleaned = (rawUrl || '').split('?')[0].split('#')[0].replace(/\/$/, '');
    const parts = cleaned.split('/').filter(Boolean);
    return parts.length ? parts[parts.length - 1] : null;
  }
};

const fetchArticleContent = async (url: string): Promise<string | null> => {
  try {
    const slug = getSlugFromUrl(url);
    if (!slug) return null;

    // posts first
    let res = await fetch(`https://dallasexpress.com/wp-json/wp/v2/posts?slug=${encodeURIComponent(slug)}&_embed`);
    let data = await res.json();

    // fallback pages
    if (!data || !data.length) {
      res = await fetch(`https://dallasexpress.com/wp-json/wp/v2/pages?slug=${encodeURIComponent(slug)}&_embed`);
      data = await res.json();
    }

    if (data && data.length > 0) {
      const rawContent = data[0].content?.rendered || '';

      const clean = rawContent
        .replace(/<\/p>/gi, '\n\n')
        .replace(/<br\s*\/?>(?!\n)/gi, '\n')
        .replace(/<img[^>]*>/gi, '')
        .replace(/<iframe[^>]*>[\s\S]*?<\/iframe>/gi, '')
        .replace(/<script\b[^>]*>([\s\S]*?)<\/script>/gim, '')
        .replace(/<[^>]+>/g, (m: string) => {
          if (m.match(/^<\/?(b|i|strong|em|u)[^>]*>/i)) return m;
          return '';
        });

      return decodeHtml(clean).trim();
    }
  } catch (e) {
    console.error('Content fetch failed', e);
  }
  return null;
};

// --- SLOTS NORMALIZATION (fix localStorage “wrong length”) ---
const normalizeSlots = (raw: any, defaults: Record<BucketKey, (Story | null)[]>) => {
  const next = { ...defaults } as Record<BucketKey, (Story | null)[]>;

  for (const b of BUCKETS) {
    const arr = Array.isArray(raw?.[b.id]) ? raw[b.id] : [];
    next[b.id] = [
      ...arr.slice(0, b.limit),
      ...Array.from({ length: Math.max(0, b.limit - arr.length) }, () => null),
    ];
  }

  if (!next.featured?.[0]) next.featured = defaults.featured;
  return next;
};

const getAssignedIds = (slots: Record<BucketKey, (Story | null)[]>) => {
  const set = new Set<string>();
  (Object.keys(slots) as BucketKey[]).forEach(k => {
    slots[k].forEach(s => {
      if (s?.id) set.add(s.id);
    });
  });
  return set;
};

const findLocation = (slots: Record<BucketKey, (Story | null)[]>, id: string) => {
  for (const k of Object.keys(slots) as BucketKey[]) {
    const idx = slots[k].findIndex(s => s?.id === id);
    if (idx !== -1) return { bucket: k, index: idx };
  }
  return null;
};

// --- PODCAST (single banner) ---
const PODCAST_BANNER_IMAGE = 'https://newsletter.dallasexpressnews.com/uploads/204924.jpg';
const PODCAST_BANNER_FALLBACK_URL = 'https://dallasexpress.com/podcast/lets-talk-local/let-s-talk-midterms-with-mayor-eric-johnson/';
const PODCAST_DEFAULT_TITLE = "Let's Talk Midterms with Mayor Eric Johnson";
const PODCAST_DEFAULT_BODY = '';

const DEFAULT_PODCAST: PodcastInfo = {
  imageUrl: PODCAST_BANNER_IMAGE,
  url: PODCAST_BANNER_FALLBACK_URL,
  title: PODCAST_DEFAULT_TITLE,
  body: PODCAST_DEFAULT_BODY,
};

// --- MENU DEFAULTS (used when a menu field is left empty) ---
const DALLAS_MENU_DEFAULT_URL = 'https://dallasexpress.com/category/city/';
const STATE_MENU_DEFAULT_URL = 'https://dallasexpress.com/category/state/';
const CRIME_MENU_DEFAULT_URL = 'https://dallasexpress.com/category/crime/';

const EMPTY_MENU_LINKS: MenuLinks = { news: '', dallas: '', podcast: '', state: '', crime: '' };

const MENU_FIELDS: { key: keyof MenuLinks; label: string; fallbackHint: string }[] = [
  { key: 'news', label: 'News', fallbackHint: 'Empty → top story (hero) link' },
  { key: 'dallas', label: 'Dallas', fallbackHint: `Empty → ${DALLAS_MENU_DEFAULT_URL}` },
  { key: 'podcast', label: 'Podcast', fallbackHint: 'Empty → podcast banner link' },
  { key: 'state', label: 'State', fallbackHint: `Empty → ${STATE_MENU_DEFAULT_URL}` },
  { key: 'crime', label: 'Crime', fallbackHint: `Empty → ${CRIME_MENU_DEFAULT_URL}` },
];

const DFW_LAT = 32.8998;
const DFW_LON = -97.0403;
const DFW_TIMEZONE = 'America/Chicago';
const OPENWEATHER_API_KEY = '247684281f792dce3755d5108f2d9049';

type WeatherInfo = {
  date: string;
  source: 'forecast' | 'historical' | 'unavailable';
  high: number | null;
  low: number | null;
  humidity: number | null;
  cloudCover: number | null;
  windMph: number | null;
  precipitation: number | null;
  sunrise: string;
  sunset: string;
  description: string;
  icon: string;
  badge: string;
  daySummary: string;
  nightSummary: string;
};

const normalizeCategoryLabel = (value: string) =>
  decodeHtml(String(value || ''))
    .replace(/&AMP;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim();

const getTemplateStories = (slots: Record<BucketKey, (Story | null)[]>) => {
  const orderedStories = (slots.top_stories.filter(Boolean) as Story[]).slice(0, 5);
  const hero = orderedStories[0] || null;
  const gridCards = orderedStories.slice(1, 5);
  const belowWeatherStories = (slots.below_weather?.filter(Boolean) as Story[]) || [];

  return {
    hero,
    gridCards,
    belowWeatherStories,
  };
};

const getTodayInDfwIso = () => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: DFW_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());

  const year = parts.find(part => part.type === 'year')?.value || '0000';
  const month = parts.find(part => part.type === 'month')?.value || '01';
  const day = parts.find(part => part.type === 'day')?.value || '01';

  return `${year}-${month}-${day}`;
};

const getDateDiffInDays = (targetIso: string, baseIso: string) => {
  const target = new Date(`${targetIso}T00:00:00`);
  const base = new Date(`${baseIso}T00:00:00`);
  return Math.round((target.getTime() - base.getTime()) / 86400000);
};

const formatIssueDateLabel = (isoDate: string) => {
  if (!isoDate) return 'Scheduled date';

  const [year, month, day] = isoDate.split('-').map(Number);
  const dt = new Date(year, (month || 1) - 1, day || 1);

  return dt.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const formatTemperature = (value: number | null) =>
  value === null ? '--' : `${Math.round(value)}°`;

const formatPercent = (value: number | null) =>
  value === null ? '--' : `${Math.round(value)}%`;

const formatPrecipitation = (value: number | null) => {
  if (value === null) return '--';
  if (value === 0) return '0.00 in';
  return `${value.toFixed(2)} in`;
};

const getWeatherDescription = (code: number | null | undefined) => {
  switch (code) {
    case 0:
      return 'Clear sky';
    case 1:
      return 'Mainly clear';
    case 2:
      return 'Partly cloudy';
    case 3:
      return 'Overcast';
    case 45:
    case 48:
      return 'Foggy';
    case 51:
    case 53:
    case 55:
      return 'Drizzle';
    case 56:
    case 57:
      return 'Freezing drizzle';
    case 61:
    case 63:
    case 65:
      return 'Rain';
    case 66:
    case 67:
      return 'Freezing rain';
    case 71:
    case 73:
    case 75:
    case 77:
      return 'Snow';
    case 80:
    case 81:
    case 82:
      return 'Rain showers';
    case 85:
    case 86:
      return 'Snow showers';
    case 95:
      return 'Thunderstorms';
    case 96:
    case 99:
      return 'Severe storms';
    default:
      return 'Conditions unavailable';
  }
};

const getWeatherIcon = (code: number | null | undefined) => {
  if (code == 0) return '☀️';
  if (code == 1 || code == 2) return '🌤️';
  if (code == 3) return '☁️';
  if (code == 45 || code == 48) return '🌫️';
  if ([51, 53, 55, 56, 57].includes(Number(code))) return '🌦️';
  if ([61, 63, 65, 66, 67, 80, 81, 82].includes(Number(code))) return '🌧️';
  if ([71, 73, 75, 77, 85, 86].includes(Number(code))) return '🌨️';
  if ([95, 96, 99].includes(Number(code))) return '⛈️';
  return '🌤️';
};

const getWeatherBadge = (isoDate: string, source: WeatherInfo['source']) => {
  if (source === 'historical') return `Observed · ${formatIssueDateLabel(isoDate)}`;
  if (source === 'forecast') return `Forecast · ${formatIssueDateLabel(isoDate)}`;
  return formatIssueDateLabel(isoDate);
};

const DEFAULT_WEATHER_INFO = (issueDate: string): WeatherInfo => ({
  date: issueDate,
  source: 'unavailable',
  high: null,
  low: null,
  humidity: null,
  cloudCover: null,
  windMph: null,
  precipitation: null,
  sunrise: '--',
  sunset: '--',
  description: 'Weather unavailable',
  icon: '🌤️',
  badge: formatIssueDateLabel(issueDate),
  daySummary: 'Weather data will load automatically for the scheduled newsletter date.',
  nightSummary: 'Preview again in a moment if you just changed the scheduled date.',
});

// Map OpenWeatherMap weather id → Open-Meteo WMO code (for description/icon reuse)
const owmIdToWmoCode = (id: number): number => {
  if (id >= 200 && id <= 232) return 95; // thunderstorm
  if (id >= 300 && id <= 321) return 51; // drizzle
  if (id >= 500 && id <= 504) return 61; // rain
  if (id === 511) return 66; // freezing rain
  if (id >= 520 && id <= 531) return 80; // showers
  if (id >= 600 && id <= 602) return 71; // snow
  if (id === 611 || id === 612 || id === 613) return 77; // sleet
  if (id === 615 || id === 616) return 68; // rain + snow
  if (id >= 620 && id <= 622) return 85; // snow showers
  if (id >= 700 && id <= 781) return 45; // fog/mist/haze
  if (id === 800) return 0; // clear
  if (id === 801) return 1; // few clouds
  if (id === 802) return 2; // scattered clouds
  if (id === 803 || id === 804) return 3; // broken/overcast
  return 0;
};

const formatUnixTimeToLocal = (unixSeconds: number | undefined): string => {
  if (typeof unixSeconds !== 'number' || !Number.isFinite(unixSeconds)) return '--';
  const date = new Date(unixSeconds * 1000);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: DFW_TIMEZONE,
  });
};

// Get the local date (YYYY-MM-DD) for a Unix timestamp in DFW timezone
const unixToLocalDate = (unixSeconds: number): string => {
  const date = new Date(unixSeconds * 1000);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: DFW_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const y = parts.find(p => p.type === 'year')?.value;
  const m = parts.find(p => p.type === 'month')?.value;
  const d = parts.find(p => p.type === 'day')?.value;
  return `${y}-${m}-${d}`;
};

// Get local hour (0-23) for a Unix timestamp in DFW timezone
const unixToLocalHour = (unixSeconds: number): number => {
  const date = new Date(unixSeconds * 1000);
  const hourStr = new Intl.DateTimeFormat('en-US', {
    timeZone: DFW_TIMEZONE,
    hour: '2-digit',
    hour12: false,
  }).format(date);
  return parseInt(hourStr, 10) || 0;
};

const fetchWeatherForIssueDate = async (issueDate: string): Promise<WeatherInfo> => {
  const fallback = DEFAULT_WEATHER_INFO(issueDate);
  if (!issueDate) return fallback;

  const todayIso = getTodayInDfwIso();
  const dayDiff = getDateDiffInDays(issueDate, todayIso);

  if (dayDiff > 5) {
    return {
      ...fallback,
      badge: `Forecast pending · ${formatIssueDateLabel(issueDate)}`,
      daySummary: 'OpenWeatherMap free forecast data becomes available up to 5 days ahead.',
      nightSummary: 'Move the scheduled date closer or wait until the date is inside the forecast window.',
    };
  }

  if (dayDiff < 0) {
    return {
      ...fallback,
      badge: `Historical · ${formatIssueDateLabel(issueDate)}`,
      source: 'historical',
      daySummary: 'Historical weather data is not available on the free tier.',
      nightSummary: 'The newsletter will generate with fallback weather text.',
    };
  }

  const source: WeatherInfo['source'] = 'forecast';
  const params = new URLSearchParams({
    lat: String(DFW_LAT),
    lon: String(DFW_LON),
    appid: OPENWEATHER_API_KEY,
    units: 'imperial',
  });

  try {
    const res = await fetch(`https://api.openweathermap.org/data/2.5/forecast?${params.toString()}`, {
      cache: 'no-store',
    });
    if (!res.ok) throw new Error(`Weather API returned ${res.status}`);

    const data = await res.json();
    const list: Array<{
      dt: number;
      main: { temp: number; temp_min: number; temp_max: number; humidity: number };
      weather: Array<{ id: number; description: string }>;
      clouds: { all: number };
      wind: { speed: number };
      rain?: { '3h'?: number };
      snow?: { '3h'?: number };
    }> = Array.isArray(data?.list) ? data.list : [];

    const sameDayEntries = list.filter(entry => unixToLocalDate(entry.dt) === issueDate);

    if (sameDayEntries.length === 0) {
      return {
        ...fallback,
        badge: getWeatherBadge(issueDate, source),
        source,
        daySummary: 'No weather entry was returned for that scheduled date.',
        nightSummary: 'Try previewing again after the scheduled date changes finish syncing.',
      };
    }

    // Find entry closest to 3pm (15:00) local time
    let afternoon = sameDayEntries[0];
    let bestDist = Math.abs(unixToLocalHour(afternoon.dt) - 15);
    for (const entry of sameDayEntries) {
      const dist = Math.abs(unixToLocalHour(entry.dt) - 15);
      if (dist < bestDist) {
        bestDist = dist;
        afternoon = entry;
      }
    }

    const high = Math.max(...sameDayEntries.map(e => e.main.temp_max));
    const low = Math.min(...sameDayEntries.map(e => e.main.temp_min));

    const humidity = afternoon.main.humidity;
    const cloudCover = afternoon.clouds.all;
    const windMph = afternoon.wind.speed;

    const precipMm = sameDayEntries.reduce((sum, e) => {
      return sum + (e.rain?.['3h'] || 0) + (e.snow?.['3h'] || 0);
    }, 0);
    const precipitation = precipMm / 25.4;

    const owmId = afternoon.weather?.[0]?.id;
    const weatherCode = typeof owmId === 'number' ? owmIdToWmoCode(owmId) : null;
    const description = getWeatherDescription(weatherCode);
    const icon = getWeatherIcon(weatherCode);

    return {
      date: issueDate,
      source,
      high,
      low,
      humidity,
      cloudCover,
      windMph,
      precipitation,
      sunrise: formatUnixTimeToLocal(data?.city?.sunrise),
      sunset: formatUnixTimeToLocal(data?.city?.sunset),
      description,
      icon,
      badge: getWeatherBadge(issueDate, source),
      daySummary: `${description}. High near ${formatTemperature(high)} with about ${formatPrecipitation(precipitation)} of expected precipitation.`,
      nightSummary: `Low near ${formatTemperature(low)} with humidity around ${formatPercent(humidity)} and cloud cover near ${formatPercent(cloudCover)}.`,
    };
  } catch (error) {
    console.error('Weather fetch failed', error);
    return {
      ...fallback,
      badge: `Weather unavailable · ${formatIssueDateLabel(issueDate)}`,
      daySummary: 'Could not load the weather data for the scheduled date.',
      nightSummary: 'The newsletter can still be generated, but the weather block will stay in fallback mode.',
    };
  }
};

const generateHtml = (
  slots: Record<BucketKey, (Story | null)[]>,
  utmCampaignRaw: string,
  podcast: PodcastInfo = DEFAULT_PODCAST,
  weatherInfo: WeatherInfo = DEFAULT_WEATHER_INFO(getTodayInDfwIso()),
  menuLinks: MenuLinks = EMPTY_MENU_LINKS
) => {
  const utmCampaign = utmCampaignRaw.replace(/\s+/g, '_');
  const { hero, gridCards, belowWeatherStories } = getTemplateStories(slots);
  const weather = weatherInfo || DEFAULT_WEATHER_INFO(getTodayInDfwIso());

  const safe = (s: string) =>
    (s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const titleCase = (s: string) =>
    normalizeCategoryLabel(s || '')
      .toLowerCase()
      .replace(/\w/g, ch => ch.toUpperCase());

  const podcastUrlRaw = (podcast.url || '').trim() || PODCAST_BANNER_FALLBACK_URL;

  const renderHero = (story: Story | null) => {
    if (!story) return '';

    const url = withUtm(story.url, utmCampaign, 'top_story_1');
    const dek = story.dek ? safe(story.dek.replace(/\s*\[…\]/g, '...').replace(/\s*\[\u2026\]/g, '...')) : '';
    const categoryLabel = titleCase(story.category || 'News');

    return `
<tr>
  <td class="pad-sides" style="padding:0px 20px 20px 20px;">
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="width:100%;">
      ${story.image ? `
      <tr>
        <td style="padding:0 0 14px 0; font-size:0; line-height:0;">
          <a href='{{ TrackLink "${url}" . }}' style="display:block; text-decoration:none;">
            <img
              src="${safe(story.image)}"
              width="560"
              alt="${safe(story.title)}"
              style="display:block; width:100%; max-width:100%; height:auto; border:0;"
            >
          </a>
        </td>
      </tr>` : ''}
      <tr>
        <td style="padding:0 0 8px 0;">
          <div class="story-category" style="font-family: Georgia, 'Times New Roman', serif; font-size:12px; line-height:14px; font-weight:700; letter-spacing:1px; text-transform:uppercase; color:#eb512e;">
            ${safe(categoryLabel)}
          </div>
        </td>
      </tr>
      <tr>
        <td style="padding:0 0 4px 0;">
          <div class="story-title" style="font-family: Georgia, 'Times New Roman', serif; font-size:21px; line-height:28px; font-weight:800; color:#000000;">
            <a href='{{ TrackLink "${url}" . }}' style="color:#000000; text-decoration:none;">
              ${safe(story.title)}
            </a>
          </div>
        </td>
      </tr>
      ${dek ? `
      <tr>
        <td style="padding:0 0 16px 0;">
          <div style="font-family: Georgia, 'Times New Roman', serif; font-size:14px; line-height:22px; color:#111111;">
            ${dek}
          </div>
        </td>
      </tr>` : ''}
      <tr>
        <td style="padding:0;">
          <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" class="full-width-btn" style="width:100%;">
            <tr>
              <td align="center" bgcolor="#000000" style="background-color:#000000;">
                <a href='{{ TrackLink "${url}" . }}'
                   target="_blank"
                   style="display:block; width:100%; padding:14px 18px; font-family: Georgia, 'Times New Roman', serif; font-size:12px; line-height:14px; font-weight:700; letter-spacing:1px; text-transform:uppercase; color:#ffffff; text-decoration:none; box-sizing:border-box;">
                  Read Full Story
                </a>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </td>
</tr>`;
  };

  // ✅ Single podcast banner (image left, title + description right; stacks on mobile)
  const renderPodcastBanner = () => {
    const bannerUrl = withUtm(podcastUrlRaw, utmCampaign, 'podcast_banner');
    const bannerImage = safe(podcast.imageUrl || PODCAST_BANNER_IMAGE);
    const pTitle = safe(podcast.title || PODCAST_DEFAULT_TITLE);
    const pBody = safe(podcast.body || '');

    return `
<tr>
  <td class="pad-sides card-grid-top card-grid-bottom" style="padding:10px 20px 10px 20px;">
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="width:100%; background:#f3f3f3;">
      <tr>
        <td class="stack" width="40%" valign="top" style="vertical-align:top;">
          <div style="padding:16px 16px 0 16px;">
            <a href='{{ TrackLink "${bannerUrl}" . }}' style="display:block; text-decoration:none;">
              <img
                class="img-full"
                src="${bannerImage}"
                alt="${pTitle}"
                style="display:block; width:100%; height:auto; border:0;">
            </a>
          </div>
        </td>
        <td class="stack" width="60%" valign="middle" style="vertical-align:middle;">
          <div style="padding:16px;">
            <div style="font-family:Georgia, 'Times New Roman', serif; font-size:17px; line-height:22px; font-weight:700; color:#000000; padding-bottom:8px;">
              <a href='{{ TrackLink "${bannerUrl}" . }}' style="color:#000000; text-decoration:none;">
                ${pTitle}
              </a>
            </div>
            ${pBody ? `
            <div style="font-family:Georgia, 'Times New Roman', serif; font-size:14px; line-height:21px; color:#42586d;">
              ${pBody}
            </div>` : ''}
          </div>
        </td>
      </tr>
    </table>
  </td>
</tr>`;
  };

  const renderCard = (story: Story, visualIndex: number) => {
    const url = withUtm(story.url, utmCampaign, `top_story_${visualIndex}`);
    const category = safe(normalizeCategoryLabel((story.category || 'NEWS').toUpperCase()));

    return `
<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" class="story-card-table" style="width:100%; background-color:#f3f3f3; height:100%;">
  ${story.image ? `
  <tr>
    <td style="padding:0; font-size:0; line-height:0;">
      <a href='{{ TrackLink "${url}" . }}' style="display:block; text-decoration:none;">
        <img
          src="${safe(story.image)}"
          alt="${safe(story.title)}"
          class="img-full"
          width="100%"
          style="display:block; width:100%; max-width:100%; height:auto; border:0;">
      </a>
    </td>
  </tr>` : ''}
  <tr>
    <td style="padding:14px 14px 0 14px;">
      <div style="font-family: Georgia, 'Times New Roman', serif; font-size:14px; color:#eb512e; text-transform:uppercase; line-height:18px; margin:0;">
        ${category}
      </div>
    </td>
  </tr>
  <tr>
    <td class="story-card-title-cell" style="padding:8px 14px 0 14px; vertical-align:top;">
      <div style="font-family: Georgia, Times, 'Times New Roman', serif; font-size:16px; font-weight:700; line-height:21px; color:#000000; margin:0;">
        <a href='{{ TrackLink "${url}" . }}' style="color:#000000; text-decoration:none;">
          ${safe(story.title)}
        </a>
      </div>
    </td>
  </tr>
  <tr>
    <td style="padding:0 14px 14px 14px;"></td>
  </tr>
</table>`;
  };

  const renderCardRow = (stories: Story[], visualStartIndex: number, padClass: string, padding: string) => {
    if (!stories.length) return '';

    const left = stories[0]
      ? `
        <td class="stack ${stories[1] ? 'stack-pad' : ''}" width="${stories[1] ? '50%' : '100%'}" valign="top" style="vertical-align:top;${stories[1] ? ' padding-right:10px;' : ''}">
          ${renderCard(stories[0], visualStartIndex)}
        </td>`
      : '';

    const right = stories[1]
      ? `
        <td class="stack" width="50%" valign="top" style="vertical-align:top; padding-left:10px;">
          ${renderCard(stories[1], visualStartIndex + 1)}
        </td>`
      : '';

    return `
<tr>
  <td class="pad-sides ${padClass}" style="padding:${padding};">
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="width:100%;">
      <tr>
        ${left}
        ${right}
      </tr>
    </table>
  </td>
</tr>`;
  };

  const formatRainInches = (value: number | null) =>
    value === null ? '--' : `${value.toFixed(2)}"`;

  const weatherSourceLabel =
    weather.source === 'historical' ? 'Observed' : weather.source === 'forecast' ? 'Forecast' : 'Weather';

  const weatherCard = `
<tr>
  <td class="pad-sides" style="padding:0px 20px 16px 20px;">

    <!-- MAIN WEATHER CARD -->
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="width:100%; background:#eef5fa; border-radius:18px; overflow:hidden;">

      <!-- HERO -->
      <tr>
        <td style="padding:18px 18px 16px 18px;">
          <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0">
            <tr>

              <!-- WEATHER ICON -->
              <td valign="middle" width="68" style="width:68px;">
                <table role="presentation" width="56" height="56" cellspacing="0" cellpadding="0" border="0" style="width:56px; height:56px; background:#d9eaf5; border-radius:28px;">
                  <tr>
                    <td align="center" valign="middle" style="font-size:27px;line-height:27px;">
                      ${safe(weather.icon)}
                    </td>
                  </tr>
                </table>
              </td>

              <!-- TEMPERATURE -->
              <td valign="middle" style="padding-left:7px;">
                <div style="font-family:Georgia,'Times New Roman',serif; font-size:11px; line-height:13px; font-weight:700; letter-spacing:1.2px; text-transform:uppercase; color:#607d95;">
                  DFW WEATHER
                </div>
                <div style="font-family:Georgia,'Times New Roman',serif; font-size:30px; line-height:31px; font-weight:700; color:#102033; padding-top:1px; white-space:nowrap;">
                  ${formatTemperature(weather.high)}
                  <span style="font-size:17px; color:#708499; font-weight:600;">
                    / ${formatTemperature(weather.low)}
                  </span>
                </div>
                <div style="font-family:Georgia,'Times New Roman',serif; font-size:14px; line-height:19px; color:#425d74;">
                  ${safe(weather.description)}
                </div>
              </td>

              <!-- FORECAST -->
              <td valign="middle" align="right" width="128" style="width:128px;">
                <table role="presentation" cellspacing="0" cellpadding="0" border="0" align="right" style="background:#d9eaf5; border-radius:13px;">
                  <tr>
                    <td style="padding:9px 13px; font-family:Georgia,'Times New Roman',serif; text-align:left; color:#244863; white-space:nowrap;">
                      <div style="font-size:11px; line-height:14px; font-weight:700;">
                        ${safe(weatherSourceLabel)}
                      </div>
                      <div style="font-size:12px; line-height:17px; color:#55718a;">
                        ${safe(formatIssueDateLabel(weather.date))}
                      </div>
                    </td>
                  </tr>
                </table>
              </td>

            </tr>
          </table>
        </td>
      </tr>

      <!-- FOUR METRICS -->
      <tr>
        <td style="padding:0 10px;">
          <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0">
            <tr>

              <td width="25%" align="center" valign="top" style="width:25%; padding:12px 4px 14px 4px; border-top:1px solid #d3e2ec;">
                <div style="font-family:Georgia,'Times New Roman',serif; font-size:10px; line-height:13px; font-weight:700; letter-spacing:.7px; text-transform:uppercase; color:#6d8497;">
                  Humidity
                </div>
                <div style="font-family:Georgia,'Times New Roman',serif; font-size:15px; line-height:20px; font-weight:700; color:#102033; padding-top:2px;">
                  ${formatPercent(weather.humidity)}
                </div>
              </td>

              <td width="25%" align="center" valign="top" style="width:25%; padding:12px 4px 14px 4px; border-top:1px solid #d3e2ec; border-left:1px solid #d3e2ec;">
                <div style="font-family:Georgia,'Times New Roman',serif; font-size:10px; line-height:13px; font-weight:700; letter-spacing:.7px; text-transform:uppercase; color:#6d8497;">
                  Rain
                </div>
                <div style="font-family:Georgia,'Times New Roman',serif; font-size:15px; line-height:20px; font-weight:700; color:#102033; padding-top:2px;">
                  ${formatRainInches(weather.precipitation)}
                </div>
              </td>

              <td width="25%" align="center" valign="top" style="width:25%; padding:12px 4px 14px 4px; border-top:1px solid #d3e2ec; border-left:1px solid #d3e2ec;">
                <div style="font-family:Georgia,'Times New Roman',serif; font-size:10px; line-height:13px; font-weight:700; letter-spacing:.7px; text-transform:uppercase; color:#6d8497;">
                  Sunrise
                </div>
                <div style="font-family:Georgia,'Times New Roman',serif; font-size:15px; line-height:20px; font-weight:700; color:#102033; padding-top:2px;">
                  ${safe(weather.sunrise)}
                </div>
              </td>

              <td width="25%" align="center" valign="top" style="width:25%; padding:12px 4px 14px 4px; border-top:1px solid #d3e2ec; border-left:1px solid #d3e2ec;">
                <div style="font-family:Georgia,'Times New Roman',serif; font-size:10px; line-height:13px; font-weight:700; letter-spacing:.7px; text-transform:uppercase; color:#6d8497;">
                  Sunset
                </div>
                <div style="font-family:Georgia,'Times New Roman',serif; font-size:15px; line-height:20px; font-weight:700; color:#102033; padding-top:2px;">
                  ${safe(weather.sunset)}
                </div>
              </td>

            </tr>
          </table>
        </td>
      </tr>

      <!-- COMPACT SUMMARY -->
      <tr>
        <td style="padding:5px 14px 14px 14px;">
          <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background:#dbeaf5; border-radius:11px;">
            <tr>
              <td style="padding:10px 13px; font-family:Georgia,'Times New Roman',serif; font-size:12px; line-height:18px; color:#3f5d73;">
                <strong style="color:#102033;">Daytime:</strong> ${safe(weather.daySummary)}<br>
                <strong style="color:#102033;">Evening:</strong> ${safe(weather.nightSummary)}
              </td>
            </tr>
          </table>
        </td>
      </tr>

    </table>
  </td>
</tr>`;

  const renderBelowWeatherStory = (story: Story, index: number) => {
    const url = withUtm(story.url, utmCampaign, `below_weather_${index + 1}`);
    const dek = (story.dek || '')
      .replace(/\n\n+/g, '<br><br>')
      .replace(/\n/g, '<br>');

    return `
<tr>
  <td class="pad-sides" style="padding:16px 20px 20px 20px;">
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="width:100%;">
      <tr>
        <td style="padding:0 0 14px 0;">
          <div class="story-title" style="font-family: Georgia, 'Times New Roman', serif; font-size:19px; line-height:26px; font-weight:800; color:#000000;">
            <a href='{{ TrackLink "${url}" . }}' style="color:#000000; text-decoration:none;">
              ${safe(story.title)}
            </a>
          </div>
        </td>
      </tr>
      ${dek ? `
      <tr>
        <td style="padding:0 0 16px 0;">
          <div style="font-family: Georgia, 'Times New Roman', serif; font-size:14px; line-height:22px; color:#111111;">
            ${dek}
          </div>
        </td>
      </tr>` : ''}
    </table>
  </td>
</tr>`;
  };

  const belowWeatherHtml = belowWeatherStories.map((s, i) => renderBelowWeatherStory(s, i)).join('');

  // ✅ MENU: News | Dallas | Podcast | State | Crime (each editable, with fallbacks)
  const pick = (value: string, fallback: string) => (value || '').trim() || fallback;
  const menuItems = [
    { label: 'News', url: pick(menuLinks.news, hero?.url || 'https://dallasexpress.com/') },
    { label: 'Dallas', url: pick(menuLinks.dallas, DALLAS_MENU_DEFAULT_URL) },
    { label: 'Podcast', url: pick(menuLinks.podcast, podcastUrlRaw) },
    { label: 'State', url: pick(menuLinks.state, STATE_MENU_DEFAULT_URL) },
    { label: 'Crime', url: pick(menuLinks.crime, CRIME_MENU_DEFAULT_URL) },
  ];

  const menuHtml = menuItems
    .map(
      item =>
        `<a class="menu-link" href='{{ TrackLink "${withUtm(item.url, utmCampaign, `menu_${item.label.toLowerCase()}`)}" . }}' target="_blank" style="color:#ffffff; text-decoration:none;">${item.label}</a>`
    )
    .join(`
              <span class="menu-sep" style="padding:0 6px; color:#6f6f6f;">|</span>
              `);

  // TOP banner — Ben Abbott (static)
  const benAbbottUrl = withUtm('https://helloben.net/', utmCampaign, 'partner_header');

  // MID banner — alternates daily between Basin Ventures and Clay Cooley
  const rotatingBanners = [
    {
      url: withUtm('https://www.basinventures.com/invest-with-basin-ventures', utmCampaign, 'partner_mid'),
      desktop: 'https://newsletter.dallasexpressnews.com/uploads/Basin.jpg',
      mobile: 'https://newsletter.dallasexpressnews.com/uploads/83765134-cc5e-497d-b3b6-93a05d245dc6.jpg',
      alt: 'Basin Ventures',
    },
    {
      url: withUtm('https://www.claycooley.com/', utmCampaign, 'partner_mid'),
      desktop: 'https://newsletter.dallasexpressnews.com/uploads/ClayCooley.jpg',
      mobile: 'https://newsletter.dallasexpressnews.com/uploads/claycooleymobile.jpg',
      alt: 'Clay Cooley',
    },
  ];
  const scheduledDate = new Date(weather.date + 'T12:00:00');
  const rotationCount = rotatingBanners.length;
  const rotationIndex = ((Math.floor(scheduledDate.getTime() / 86400000) % rotationCount) + rotationCount) % rotationCount;
  const midBanner = rotatingBanners[rotationIndex];

  return `<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" align="center" style="width:100%; max-width:100%; table-layout:fixed; word-break:break-word;">

  <!-- PARTNER BANNER (TOP) — Ben Abbott -->
  <tr>
    <td align="center" class="px" style="padding-top:0px; padding-bottom:12px; text-align:center;">
      <a href='{{ TrackLink "${benAbbottUrl}" . }}' target="_blank" style="text-decoration:none; display:block;">
        <!-- Desktop -->
        <img
          src="https://newsletter.dallasexpressnews.com/uploads/benabbottdesktop.jpg"
          alt="Ben Abbott"
          width="602"
          class="Ad-desktop"
          style="width:100%; max-width:602px; height:auto; display:block; border:0;">
        <!-- Mobile -->
        <img
          src="https://newsletter.dallasexpressnews.com/uploads/benabbottmobile.jpg"
          alt="Ben Abbott"
          width="602"
          class="Ad-mobile"
          style="width:100%; max-width:602px; height:auto; display:none; border:0;">
      </a>
    </td>
  </tr>

  <!-- SPACER -->
  <tr>
    <td height="25" style="font-size:0; line-height:12px;">&nbsp;</td>
  </tr>

  <!-- LOGO -->
  <tr>
    <td align="center" class="px" style="padding-top:0; padding-bottom:0;">
      <img
        alt="The Dallas Express News"
        src="https://newsletter.dallasexpressnews.com/uploads/DX-LOGO.png"
        style="width:300px; max-width:100%; height:auto; display:inline-block;">
    </td>
  </tr>

  <!-- DATE -->
  <tr>
    <td align="center" class="px" style="padding-top:12px; padding-bottom:18px;">
      <div style="font-family: Georgia,'Times New Roman',serif; font-size:13px; line-height:20px; color:#000000; letter-spacing:0.1px;">
        {{ Date "Monday, January 2, 2006" }}
      </div>
    </td>
  </tr>

  <!-- MENU -->
  <tr>
    <td class="px" style="padding-top:0; padding-bottom:14px;">
      <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0">
        <tr>
          <td align="center" bgcolor="#000000" class="menu-bar" style="padding:14px 24px; background-color:#000000;">
            <div class="menu-text" style="font-family:Arial, Helvetica, sans-serif; font-size:11px; line-height:16px; color:#ffffff; text-transform:uppercase; letter-spacing:1px; text-align:center; white-space:nowrap;">
              ${menuHtml}
            </div>
          </td>
        </tr>
      </table>
    </td>
  </tr>

  ${renderHero(hero)}
  ${weatherCard}
  ${renderCardRow(gridCards.slice(0, 2), 2, 'card-grid-top', '10px 20px 10px 20px')}
  ${renderPodcastBanner()}
  ${renderCardRow(gridCards.slice(2, 4), 4, 'card-grid-bottom', '10px 20px 10px 20px')}
  <tr><td class="pad-sides" style="padding:0 20px;"><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"><tr><td style="border-top:1px solid #cccccc; font-size:0; line-height:0;" height="1">&nbsp;</td></tr></table></td></tr>

  <!-- PARTNER BANNER MID — alternates daily: Basin / Clay Cooley -->
  <tr>
    <td align="center" class="px" style="padding-top:12px; padding-bottom:12px; text-align:center;">
      <a href='{{ TrackLink "${midBanner.url}" . }}' target="_blank" style="text-decoration:none; display:block;">
        <!-- Desktop -->
        <img
          src="${midBanner.desktop}"
          alt="${midBanner.alt}"
          width="602"
          class="Ad-desktop"
          style="width:100%; max-width:602px; height:auto; display:block; border:0;">
        <!-- Mobile -->
        <img
          src="${midBanner.mobile}"
          alt="${midBanner.alt}"
          width="602"
          class="Ad-mobile"
          style="width:100%; max-width:602px; height:auto; display:none; border:0;">
      </a>
    </td>
  </tr>

  <tr><td class="pad-sides" style="padding:0 20px;"><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"><tr><td style="border-top:1px solid #cccccc; font-size:0; line-height:0;" height="1">&nbsp;</td></tr></table></td></tr>

  ${belowWeatherHtml}
</table>
`;
};

// --- DEV TESTS ---
const runDevTests = () => {
  console.assert(JSON.stringify(parseListIds('1')) === JSON.stringify([1]), 'parseListIds single');
  console.assert(JSON.stringify(parseListIds('1,2,2, 3')) === JSON.stringify([1, 2, 3]), 'parseListIds dedupe');
  console.assert(JSON.stringify(parseListIds('0, -1, abc, 7')) === JSON.stringify([7]), 'parseListIds filters');

  const u1 = withUtm('https://example.com/path', 'camp', 'c1');
  console.assert(u1.includes('utm_source=') && u1.includes('utm_medium=') && u1.includes('utm_campaign='), 'withUtm adds');
  const u2 = withUtm('not-a-url', 'camp', 'c1');
  console.assert(u2 === 'not-a-url', 'withUtm ignores invalid');
};

try {
  if (typeof window !== 'undefined') runDevTests();
} catch {
  // ignore
}

// --- MAIN APP COMPONENT ---
export default function NewsletterApp() {
  const [view, setView] = useState<'editor' | 'preview'>('editor');
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [showSettings, setShowSettings] = useState(false);
  const [isPushing, setIsPushing] = useState(false);

  // Newsletter Info
  const [issueDate, setIssueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });

  const [issueTime, setIssueTime] = useState('06:00');
  const [campaignName, setCampaignName] = useState('');
  const [subject, setSubject] = useState('');

  const [apiInfo, setApiInfo] = useState({ url: '', username: '', password: '', listIds: [1] as number[] });
  const [listIdsInput, setListIdsInput] = useState('1');

  // ✅ One podcast banner
  const [podcast, setPodcast] = useState<PodcastInfo>(DEFAULT_PODCAST);
  const updatePodcast = (field: keyof PodcastInfo, value: string) =>
    setPodcast(prev => ({ ...prev, [field]: value }));

  // ✅ Editable menu links
  const [menuLinks, setMenuLinks] = useState<MenuLinks>(EMPTY_MENU_LINKS);
  const updateMenuLink = (field: keyof MenuLinks, value: string) =>
    setMenuLinks(prev => ({ ...prev, [field]: value }));

  const [weatherInfo, setWeatherInfo] = useState<WeatherInfo>(() => DEFAULT_WEATHER_INFO(issueDate));
  const [isWeatherLoading, setIsWeatherLoading] = useState(false);

  const DEFAULT_SLOTS: Record<BucketKey, (Story | null)[]> = {
    top_stories: [null, null, null, null, null],
    featured: [null],
    trending: [null, null, null, null, null],
    editors_pick: [null],
    below_weather: [null],
  };

  const [stories, setStories] = useState<Story[]>([]);
  const [slots, setSlots] = useState<Record<BucketKey, (Story | null)[]>>(DEFAULT_SLOTS);
  const [storyToEdit, setStoryToEdit] = useState<Story | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 6 } })
  );

  useEffect(() => {
    const savedApi = localStorage.getItem('dx_api_info');
    if (savedApi) {
      const parsed = JSON.parse(savedApi);

      const listIds = Array.isArray(parsed.listIds)
        ? parsed.listIds.map((n: any) => Number(n)).filter((n: any) => Number.isFinite(n) && n > 0)
        : parsed.listId
          ? [Number(parsed.listId)]
          : [1];

      setApiInfo({
        url: parsed.url || '',
        username: parsed.username || '',
        password: parsed.password || '',
        listIds: listIds.length ? Array.from(new Set(listIds)) : [1],
      });
    }

    const savedSlotsRaw = localStorage.getItem('dx_slots');
    if (savedSlotsRaw) {
      const parsed = JSON.parse(savedSlotsRaw);
      setSlots(normalizeSlots(parsed, DEFAULT_SLOTS));
    } else {
      setSlots(DEFAULT_SLOTS);
    }

    const savedStories = localStorage.getItem('dx_stories');
    if (savedStories) setStories(JSON.parse(savedStories));

    const savedPodcastBanner = localStorage.getItem('dx_podcast_banner');
    if (savedPodcastBanner) {
      try {
        const parsed = JSON.parse(savedPodcastBanner);
        setPodcast(prev => ({
          ...prev,
          ...(parsed?.imageUrl ? { imageUrl: parsed.imageUrl } : {}),
          ...(parsed?.url ? { url: parsed.url } : {}),
        }));
      } catch {
        // ignore invalid saved podcast banner config
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('dx_slots', JSON.stringify(slots));
  }, [slots]);

  useEffect(() => {
    localStorage.setItem('dx_stories', JSON.stringify(stories));
  }, [stories]);

  useEffect(() => {
    localStorage.setItem('dx_podcast_banner', JSON.stringify({ imageUrl: podcast.imageUrl, url: podcast.url }));
  }, [podcast.imageUrl, podcast.url]);

  // Load saved banner + menu config from server on mount
  useEffect(() => {
    fetch('/api/config')
      .then(r => (r.ok ? r.json() : null))
      .then(cfg => {
        if (!cfg) return;

        if (cfg.podcast) {
          setPodcast(prev => ({
            imageUrl: cfg.podcast.imageUrl || prev.imageUrl,
            url: cfg.podcast.url || prev.url,
            title: cfg.podcast.title || prev.title,
            body: typeof cfg.podcast.body === 'string' ? cfg.podcast.body : prev.body,
          }));
        }

        if (cfg.menu && typeof cfg.menu === 'object') {
          setMenuLinks(prev => {
            const next = { ...prev };
            (Object.keys(next) as (keyof MenuLinks)[]).forEach(k => {
              if (typeof cfg.menu[k] === 'string') next[k] = cfg.menu[k];
            });
            return next;
          });
        } else if (cfg.dallas && typeof cfg.dallas.url === 'string') {
          // legacy config
          setMenuLinks(prev => ({ ...prev, dallas: cfg.dallas.url }));
        }
      })
      .catch(() => {});
  }, []);

  const [bannerSaveStatus, setBannerSaveStatus] = useState<string | null>(null);

  const saveBannerConfig = async () => {
    setBannerSaveStatus('Saving...');
    try {
      const res = await fetch('/api/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          podcast,
          menu: menuLinks,
        }),
      });
      if (res.ok) {
        setBannerSaveStatus('Saved!');
        setTimeout(() => setBannerSaveStatus(null), 2000);
      } else {
        setBannerSaveStatus('Save failed');
        setTimeout(() => setBannerSaveStatus(null), 3000);
      }
    } catch {
      setBannerSaveStatus('Save failed');
      setTimeout(() => setBannerSaveStatus(null), 3000);
    }
  };

  useEffect(() => {
    let cancelled = false;

    setIsWeatherLoading(true);

    fetchWeatherForIssueDate(issueDate)
      .then(data => {
        if (!cancelled) setWeatherInfo(data);
      })
      .catch(() => {
        if (!cancelled) setWeatherInfo(DEFAULT_WEATHER_INFO(issueDate));
      })
      .finally(() => {
        if (!cancelled) setIsWeatherLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [issueDate]);

  useEffect(() => {
    const [y, m, d] = issueDate.split('-');
    setCampaignName(`${m}.${d}.${y.slice(2)} Newsletter`);
  }, [issueDate]);

  useEffect(() => {
    const firstStory = slots.top_stories.find(Boolean) as Story | undefined;
    if (firstStory) setSubject(firstStory.title);
  }, [slots.top_stories]);

  useEffect(() => {
    if (showSettings) {
      const ids = apiInfo.listIds?.length ? apiInfo.listIds : [1];
      setListIdsInput(ids.join(','));
    }
  }, [showSettings, apiInfo.listIds]);

  const assignedIds = useMemo(() => getAssignedIds(slots), [slots]);

  // ✅ SINGLE SOURCE OF TRUTH FOR OUTPUT HTML (preview/copy/push all match)
  const generatedHtml = useMemo(
    () => generateHtml(slots, campaignName, podcast, weatherInfo, menuLinks),
    [slots, campaignName, podcast, weatherInfo, menuLinks]
  );

  const previewHtml = useMemo(() => {
    return generatedHtml
      .replace(/{{\s*define\s+"content"\s*}}/g, '')
      .replace(/{{\s*end\s*}}/g, '')
      .replace(/{{\s*TrackLink\s+"([^"]+)"\s+\.\s*}}/g, '$1')
      .trim();
  }, [generatedHtml]);

  const saveSettings = () => {
    let cleanUrl = apiInfo.url.trim();
    if (cleanUrl.endsWith('/')) cleanUrl = cleanUrl.slice(0, -1);

    const parsedIds = parseListIds(listIdsInput);
    const finalIds = parsedIds.length ? parsedIds : [1];

    const newApiInfo = { ...apiInfo, url: cleanUrl, listIds: finalIds };
    setApiInfo(newApiInfo);
    localStorage.setItem('dx_api_info', JSON.stringify(newApiInfo));
    setShowSettings(false);
  };

  const addStory = (story: Story) => {
    setSlots(currSlots => {
      const next = { ...currSlots } as Record<BucketKey, (Story | null)[]>;
      (Object.keys(next) as BucketKey[]).forEach(key => {
        next[key] = next[key].map(s => (s?.id === story.id ? story : s));
      });
      return next;
    });

    setStories(prev => {
      const existingIdx = prev.findIndex(s => s.id === story.id);
      if (existingIdx !== -1) {
        const next = [...prev];
        next[existingIdx] = story;
        return next;
      }

      if (story.id === 'default-podcast') return prev;
      if (prev.some(s => s.url === story.url)) return prev;

      return [story, ...prev];
    });

    setStoryToEdit(null);
  };

  const clearAllArticles = () => {
    const ok = confirm('Clear all selected stories and inbox items?');
    if (!ok) return;

    setStoryToEdit(null);
    setStories([]);
    setSubject('');

    setSlots(prev => {
      const next = { ...prev };
      (Object.keys(next) as BucketKey[]).forEach(key => {
        if (key === 'featured') return;
        const cfg = BUCKETS.find(b => b.id === key);
        const len = cfg?.limit ?? next[key].length;
        next[key] = Array.from({ length: len }, () => null);
      });
      return next;
    });
  };

  const deleteStory = (id: string) => {
    setStories(prev => prev.filter(s => s.id !== id));
    setSlots(prev => {
      const next = { ...prev };
      (Object.keys(next) as BucketKey[]).forEach(key => {
        next[key] = next[key].map(s => (s?.id === id ? null : s));
      });
      return next;
    });
  };

  const assignToSlot = async (story: Story, bucket: BucketKey) => {
    let storyToAssign = { ...story };

    if (bucket === 'editors_pick' || bucket === 'below_weather') {
      const fullContent = await fetchArticleContent(story.url);
      if (fullContent) storyToAssign.dek = fullContent;

      setStories(prev => prev.map(s => (s.id === storyToAssign.id ? storyToAssign : s)));
    }

    setSlots(prev => {
      const next = { ...prev } as Record<BucketKey, (Story | null)[]>;

      (Object.keys(next) as BucketKey[]).forEach(k => {
        next[k] = next[k].map(s => (s?.id === storyToAssign.id ? null : s));
      });

      const config = BUCKETS.find(b => b.id === bucket);
      if (!config) return prev;

      const currentList = [...next[bucket]];

      if (config.limit === 1) {
        currentList[0] = storyToAssign;
        next[bucket] = currentList;
        return next;
      }

      const emptyIdx = currentList.findIndex(s => s === null);
      if (emptyIdx !== -1) {
        currentList[emptyIdx] = storyToAssign;
      } else {
        alert(`${config.label} is full! Clear a slot first.`);
        return prev;
      }

      next[bucket] = currentList;
      return next;
    });
  };

  const removeFromSlot = (bucket: BucketKey, index: number) => {
    setSlots(prev => {
      const next = { ...prev };
      next[bucket] = [...next[bucket]];
      next[bucket][index] = null;
      return next;
    });
  };

  const moveSlot = (bucket: BucketKey, from: number, direction: 'up' | 'down') => {
    setSlots(prev => {
      const next = { ...prev };
      const list = [...next[bucket]];
      const to = direction === 'up' ? from - 1 : from + 1;

      if (to < 0 || to >= list.length) return prev;

      const temp = list[from];
      list[from] = list[to];
      list[to] = temp;

      next[bucket] = list;
      return next;
    });
  };

  const handleDragEnd = async (e: DragEndEvent) => {
    const activeId = String(e.active.id);
    const overId = e.over ? String(e.over.id) : null;
    if (!overId) return;

    if (!activeId.startsWith('story:')) return;
    const storyId = activeId.replace('story:', '');

    if (overId === 'drop:inbox') {
      setSlots(prev => {
        const next = { ...prev } as Record<BucketKey, (Story | null)[]>;
        const loc = findLocation(next, storyId);
        if (loc) next[loc.bucket] = next[loc.bucket].map((s, i) => (i === loc.index ? null : s));
        return next;
      });
      return;
    }

    if (overId.startsWith('drop:slot:')) {
      const [, , bucketKey, idxStr] = overId.split(':');
      const bucket = bucketKey as BucketKey;
      const idx = Number(idxStr);

      const storyObj =
        stories.find(s => s.id === storyId) ||
        (Object.values(slots).flat().find(s => s?.id === storyId) as Story | undefined);

      if (!storyObj) return;

      // Must Read needs the full article body → fetch it like the button does
      if (bucket === 'editors_pick' || bucket === 'below_weather') {
        await assignToSlot(storyObj, bucket);
        return;
      }

      setSlots(prev => {
        const next = { ...prev } as Record<BucketKey, (Story | null)[]>;

        (Object.keys(next) as BucketKey[]).forEach(k => {
          next[k] = next[k].map(s => (s?.id === storyId ? null : s));
        });

        const list = [...next[bucket]];
        list[idx] = storyObj;
        next[bucket] = list;
        return next;
      });

      return;
    }

    if (overId.startsWith('drop:bucket:')) {
      const bucket = overId.replace('drop:bucket:', '') as BucketKey;

      const storyObj =
        stories.find(s => s.id === storyId) ||
        (Object.values(slots).flat().find(s => s?.id === storyId) as Story | undefined);

      if (!storyObj) return;

      await assignToSlot(storyObj, bucket);
      return;
    }
  };

  const buildSlotsWithN8nImages = async (
    slots: Record<BucketKey, (Story | null)[]>
  ): Promise<Record<BucketKey, (Story | null)[]>> => {
    const next: Record<BucketKey, (Story | null)[]> = {
      top_stories: [...slots.top_stories],
      featured: [...slots.featured],
      trending: [...slots.trending],
      editors_pick: [...slots.editors_pick],
      below_weather: [...slots.below_weather],
    };

    const { hero, gridCards } = getTemplateStories(next);
    const displayedIds = new Set([hero?.id, ...gridCards.map(story => story.id)].filter(Boolean));
    const imageUrls = [hero, ...gridCards]
      .filter((story): story is Story => Boolean(story?.image))
      .map(story => story.image as string);

    if (!imageUrls.length) return next;

    const res = await fetch(N8N_UPLOAD_WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        imageUrls,
      }),
    });

    const data = await res.json().catch(() => null);

    if (!res.ok) {
      throw new Error(`n8n upload failed: ${res.status} ${JSON.stringify(data)}`);
    }

    if (!Array.isArray(data)) {
      throw new Error(`n8n response was not an array: ${JSON.stringify(data)}`);
    }

    const uploadedUrls = data
      .map((item: any) => item?.uploadedUrl)
      .filter((url: any) => typeof url === 'string' && url.length > 0);

    if (uploadedUrls.length !== imageUrls.length) {
      throw new Error(
        `n8n returned ${uploadedUrls.length} uploaded URLs for ${imageUrls.length} images`
      );
    }

    let uploadIndex = 0;

    (Object.keys(next) as BucketKey[]).forEach(bucket => {
      next[bucket] = next[bucket].map(story => {
        if (!story?.image || !displayedIds.has(story.id)) return story;

        const uploadedUrl = uploadedUrls[uploadIndex];
        uploadIndex += 1;

        return {
          ...story,
          image: uploadedUrl,
        };
      });
    });

    return next;
  };

  const pushToListmonk = async () => {
    if (!apiInfo.url || !apiInfo.username) {
      alert('Please configure API settings first.');
      setShowSettings(true);
      return;
    }

    if (!apiInfo.listIds?.length) {
      alert('Please add at least one List ID.');
      setShowSettings(true);
      return;
    }

    setIsPushing(true);

    const sendAt = new Date(`${issueDate}T${issueTime}:00`).toISOString();

    try {
      const base = apiInfo.url.replace(/\/+$/, '');
      const auth = btoa(`${apiInfo.username}:${apiInfo.password}`);

      // 1) Upload top story images through n8n
      const slotsForSend = await buildSlotsWithN8nImages(slots);

      // 2) Generate HTML using Listmonk-hosted image URLs
      const latestWeather = await fetchWeatherForIssueDate(issueDate);
      const html = generateHtml(slotsForSend, campaignName, podcast, latestWeather, menuLinks);

      // 3) Create campaign in Listmonk
      const payload = {
        name: campaignName,
        subject: subject,
        body: html,
        type: 'regular',
        content_type: 'html',
        lists: apiInfo.listIds,
        tags: ['newsletter'],
        send_at: sendAt,
      };

      const res = await fetch(`${base}/api/campaigns`, {
        method: 'POST',
        headers: {
          Authorization: `Basic ${auth}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || res.statusText);
      }

      const data = await res.json();
      alert(`Success! Created "${campaignName}" (ID: ${data.data.id}). Check drafts.`);
    } catch (e: any) {
      console.error('PUSH FAILED:', e);
      alert(`Error pushing: ${e?.message || 'Unknown error'}`);
    } finally {
      setIsPushing(false);
    }
  };

  const copyToClipboard = () => {
    const html = generatedHtml;

    const textArea = document.createElement('textarea');
    textArea.value = html;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    textArea.style.top = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      alert(successful ? 'HTML copied to clipboard!' : 'Failed to copy HTML');
    } catch (err) {
      console.error('Fallback copy failed', err);
      alert('Failed to copy HTML manually.');
    }

    document.body.removeChild(textArea);
  };

  const copyJson = () => {
    const sendAt = new Date(`${issueDate}T${issueTime}:00`).toISOString();
    const payload = {
      name: campaignName,
      subject: subject,
      body: generatedHtml,
      type: 'regular',
      content_type: 'html',
      lists: apiInfo.listIds,
      tags: ['newsletter'],
      send_at: sendAt,
    };
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    alert('Campaign JSON copied! Use this if direct push fails.');
  };

  const inputCls =
    'w-full bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500';

  return (
    <div className="min-h-screen bg-zinc-100 text-zinc-900 font-sans">
      <header className="bg-zinc-900 text-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="font-bold text-lg">DX Daily Newsletter</h1>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setView(view === 'editor' ? 'preview' : 'editor')}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-800 hover:bg-zinc-700 text-sm font-medium transition-colors"
            >
              {view === 'editor' ? <Eye size={16} /> : <Code size={16} />}
              <span className="hidden sm:inline">{view === 'editor' ? 'Preview HTML' : 'Edit Content'}</span>
            </button>

            <button
              onClick={pushToListmonk}
              disabled={isPushing}
              className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-600 hover:bg-orange-700 text-sm font-bold shadow-lg transition-transform active:scale-95 disabled:opacity-70"
            >
              {isPushing ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              <span className="hidden sm:inline">{isPushing ? 'Pushing...' : 'Push to Draft'}</span>
            </button>

            <button
              onClick={() => setShowSettings(true)}
              className="p-2 rounded-full hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
            >
              <Settings size={20} />
            </button>
          </div>
        </div>
      </header>

      {showSettings && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Settings size={20} /> API Settings
            </h2>

            <div className="bg-yellow-50 text-yellow-800 p-3 rounded-lg text-xs mb-4 flex gap-2">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <div>
                If direct push fails due to CORS (common on mobile), use the &quot;Copy JSON&quot; button and create the
                campaign via API/Postman, or add a Next.js proxy route.
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-zinc-500 uppercase">URL</label>
                <input
                  value={apiInfo.url}
                  onChange={e => setApiInfo({ ...apiInfo, url: e.target.value })}
                  placeholder="https://newsletter.dallasexpressnews.com"
                  className="w-full border rounded-lg p-2 text-sm mt-1 focus:ring-2 focus:ring-orange-500 outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-zinc-500 uppercase">Username</label>
                  <input
                    value={apiInfo.username}
                    onChange={e => setApiInfo({ ...apiInfo, username: e.target.value })}
                    className="w-full border rounded-lg p-2 text-sm mt-1 focus:ring-2 focus:ring-orange-500 outline-none"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-zinc-500 uppercase">Password</label>
                  <input
                    type="password"
                    value={apiInfo.password}
                    onChange={e => setApiInfo({ ...apiInfo, password: e.target.value })}
                    className="w-full border rounded-lg p-2 text-sm mt-1 focus:ring-2 focus:ring-orange-500 outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-zinc-500 uppercase">List IDs (comma-separated)</label>
                <input
                  value={listIdsInput}
                  onChange={e => setListIdsInput(e.target.value)}
                  placeholder="1,2,3"
                  className="w-full border rounded-lg p-2 text-sm mt-1 focus:ring-2 focus:ring-orange-500 outline-none"
                />
                <div className="text-[11px] text-zinc-400 mt-1">Example: 1,2,7 — will send to all selected lists</div>
              </div>
            </div>

            <div className="flex justify-between gap-2 mt-6 border-t pt-4">
              <button
                onClick={copyJson}
                className="px-4 py-2 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg font-medium flex items-center gap-2"
              >
                <Copy size={16} /> Copy JSON
              </button>

              <div className="flex gap-2">
                <button
                  onClick={() => setShowSettings(false)}
                  className="px-4 py-2 text-sm text-zinc-600 hover:bg-zinc-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={saveSettings}
                  className="px-4 py-2 text-sm bg-zinc-900 text-white rounded-lg hover:bg-black font-medium"
                >
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto p-4 sm:p-6">
        {view === 'preview' ? (
          <div className="space-y-4">
            <div className="flex justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-zinc-200">
              <div className="text-sm text-zinc-500">
                Subject: <span className="font-bold text-zinc-900">{subject}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex bg-zinc-100 rounded-lg p-0.5">
                  <button
                    onClick={() => setPreviewMode('desktop')}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                      previewMode === 'desktop' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
                    }`}
                    type="button"
                  >
                    <Monitor size={14} className="inline mr-1" />Desktop
                  </button>
                  <button
                    onClick={() => setPreviewMode('mobile')}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                      previewMode === 'mobile' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
                    }`}
                    type="button"
                  >
                    <Smartphone size={14} className="inline mr-1" />Mobile
                  </button>
                </div>
                <button
                  onClick={copyToClipboard}
                  className="text-sm font-medium text-orange-600 flex items-center gap-1 hover:underline"
                >
                  <Copy size={16} /> Copy HTML
                </button>
              </div>
            </div>
            <div className={`bg-white shadow-2xl rounded-xl overflow-hidden border border-zinc-200 transition-all ${previewMode === 'mobile' ? 'max-w-[375px] mx-auto' : ''}`}>
              <iframe className="w-full h-[800px] bg-white" srcDoc={previewHtml} title="Preview" />
            </div>
          </div>
        ) : (
          <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-4 space-y-6">
                <div className="bg-white p-4 rounded-xl border border-zinc-200 shadow-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-zinc-900 text-sm uppercase tracking-wider">Campaign Info</h3>

                    <button
                      onClick={clearAllArticles}
                      className="text-xs font-bold text-red-600 hover:text-red-700 hover:underline flex items-center gap-1"
                      title="Clears inbox and all 5 selected story slots"
                    >
                      <Trash2 size={14} />
                      Clear all
                    </button>
                  </div>

                  <div className="grid grid-cols-1 gap-3">
                    <div className="flex gap-3 w-full min-w-0">
                      <div className="flex-1 min-w-0 space-y-2">
                        <label className="text-xs font-bold text-zinc-500">Scheduled Date</label>
                        <div className="bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2">
                          <input
                            type="date"
                            value={issueDate}
                            onChange={e => setIssueDate(e.target.value)}
                            className="w-full min-w-0 bg-transparent text-sm focus:outline-none"
                          />
                        </div>
                      </div>

                      <div className="flex-1 min-w-0 space-y-2">
                        <label className="text-xs font-bold text-zinc-500">Time</label>
                        <div className="bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2">
                          <input
                            type="time"
                            value={issueTime}
                            onChange={e => setIssueTime(e.target.value)}
                            className="w-full min-w-0 bg-transparent text-sm focus:outline-none"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-bold text-zinc-500">Campaign Name (Internal)</label>
                      <input
                        type="text"
                        value={campaignName}
                        onChange={e => setCampaignName(e.target.value)}
                        className={inputCls}
                      />
                    </div>
                  </div>
                </div>

                <StoryManager
                  stories={stories}
                  assignedIds={assignedIds}
                  onAdd={addStory}
                  onAssign={assignToSlot}
                  onDelete={deleteStory}
                  storyToEdit={storyToEdit}
                  onEdit={setStoryToEdit}
                />
              </div>

              <div className="lg:col-span-8 space-y-8">
                <div className="bg-white p-4 rounded-xl border border-zinc-200 shadow-sm space-y-3">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-zinc-500">Subject Line (Auto-updates)</label>
                    <input
                      type="text"
                      value={subject}
                      onChange={e => setSubject(e.target.value)}
                      className={`${inputCls} font-medium`}
                      placeholder="Will auto-fill from top story..."
                    />
                  </div>
                </div>

                {/* ✅ MENU LINKS — News | Dallas | Podcast | State | Crime */}
                <div className="bg-white p-4 rounded-xl border border-zinc-200 shadow-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-zinc-900 text-sm uppercase tracking-wider">Menu Links</h3>
                      <p className="text-[11px] text-zinc-400">News | Dallas | Podcast | State | Crime</p>
                    </div>
                    <button
                      onClick={saveBannerConfig}
                      className="px-3 py-1 text-xs font-bold text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
                    >
                      {bannerSaveStatus || 'Save'}
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {MENU_FIELDS.map(field => (
                      <div key={field.key} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-zinc-500">{field.label} URL</label>
                          {menuLinks[field.key] && (
                            <button
                              type="button"
                              onClick={() => updateMenuLink(field.key, '')}
                              className="text-[11px] text-zinc-400 hover:text-red-500"
                            >
                              Reset
                            </button>
                          )}
                        </div>
                        <input
                          type="text"
                          value={menuLinks[field.key]}
                          onChange={e => updateMenuLink(field.key, e.target.value)}
                          className={inputCls}
                          placeholder="https://..."
                        />
                        <p className="text-[11px] text-zinc-400 truncate">{field.fallbackHint}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ✅ SINGLE PODCAST BANNER */}
                <div className="bg-white p-4 rounded-xl border border-zinc-200 shadow-sm space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="font-bold text-zinc-900 text-sm uppercase tracking-wider">Podcast Banner</h3>
                    <div className="flex items-center gap-3">
                      <div className="text-[11px] font-medium text-zinc-500">
                        {isWeatherLoading ? 'Updating weather…' : `Weather synced for ${formatIssueDateLabel(weatherInfo.date)}`}
                      </div>
                      <button
                        onClick={saveBannerConfig}
                        className="px-3 py-1 text-xs font-bold text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
                      >
                        {bannerSaveStatus || 'Save'}
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-zinc-500">Thumbnail URL</label>
                      <input
                        type="text"
                        value={podcast.imageUrl}
                        onChange={e => updatePodcast('imageUrl', e.target.value)}
                        className={inputCls}
                        placeholder="https://..."
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-bold text-zinc-500">Podcast Link URL</label>
                      <input
                        type="text"
                        value={podcast.url}
                        onChange={e => updatePodcast('url', e.target.value)}
                        className={inputCls}
                        placeholder="https://..."
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-bold text-zinc-500">Podcast Title</label>
                    <input
                      type="text"
                      value={podcast.title}
                      onChange={e => updatePodcast('title', e.target.value)}
                      className={inputCls}
                      placeholder="Episode title..."
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-bold text-zinc-500">Podcast Description</label>
                    <textarea
                      value={podcast.body}
                      onChange={e => updatePodcast('body', e.target.value)}
                      rows={3}
                      className={`${inputCls} resize-y`}
                      placeholder="Episode description..."
                    />
                  </div>
                </div>

                {BUCKETS.map(bucket => (
                  <BucketSection
                    key={bucket.id}
                    config={bucket}
                    slots={slots[bucket.id]}
                    onClear={idx => removeFromSlot(bucket.id, idx)}
                    onMoveUp={idx => moveSlot(bucket.id, idx, 'up')}
                    onMoveDown={idx => moveSlot(bucket.id, idx, 'down')}
                    onEdit={setStoryToEdit}
                  />
                ))}
              </div>
            </div>
          </DndContext>
        )}
      </main>
    </div>
  );
}

// --- DROPPABLE INBOX ---
function DroppableInbox({ children }: { children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'drop:inbox' });
  return (
    <div
      ref={setNodeRef}
      className={`flex-1 overflow-y-auto p-2 space-y-2 bg-white ${isOver ? 'ring-2 ring-orange-400 rounded-lg' : ''}`}
    >
      {children}
    </div>
  );
}

// --- STORY MANAGER COMPONENT ---
function StoryManager({
  stories,
  assignedIds,
  onAdd,
  onAssign,
  onDelete,
  storyToEdit,
  onEdit,
}: {
  stories: Story[];
  assignedIds: Set<string>;
  onAdd: (s: Story) => void;
  onAssign: (s: Story, b: BucketKey) => void;
  onDelete: (id: string) => void;
  storyToEdit: Story | null;
  onEdit: (s: Story | null) => void;
}) {
  const [activeTab, setActiveTab] = useState<'fetch' | 'manual'>('fetch');

  useEffect(() => {
    if (storyToEdit) setActiveTab('manual');
  }, [storyToEdit]);

  const visibleStories = useMemo(() => stories.filter(s => !assignedIds.has(s.id)), [stories, assignedIds]);

  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm flex flex-col h-[700px]">
      <div className="flex border-b border-zinc-200 shrink-0">
        <button
          onClick={() => setActiveTab('fetch')}
          className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 ${
            activeTab === 'fetch'
              ? 'bg-orange-50 text-orange-700 border-b-2 border-orange-500'
              : 'text-zinc-500 hover:bg-zinc-50'
          }`}
        >
          <Globe size={16} /> Fetch
        </button>
        <button
          onClick={() => setActiveTab('manual')}
          className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 ${
            activeTab === 'manual'
              ? 'bg-orange-50 text-orange-700 border-b-2 border-orange-500'
              : 'text-zinc-500 hover:bg-zinc-50'
          }`}
        >
          <LinkIcon size={16} /> Manual
        </button>
      </div>

      <div className="p-4 bg-zinc-50 border-b border-zinc-200 shrink-0">
        {activeTab === 'fetch' ? (
          <FetcherForm onAdd={onAdd} />
        ) : (
          <ManualForm onAdd={onAdd} storyToEdit={storyToEdit} onDone={() => onEdit(null)} />
        )}
      </div>

      <DroppableInbox>
        {visibleStories.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-zinc-400 text-sm italic opacity-60">
            <div className="mb-2">
              <FileText size={24} />
            </div>
            <div>Unassigned stories will appear here</div>
          </div>
        ) : (
          visibleStories.map(story => (
            <StoryCard
              key={story.id}
              story={story}
              onAssign={bucket => onAssign(story, bucket)}
              onDelete={() => onDelete(story.id)}
              onEdit={() => onEdit(story)}
            />
          ))
        )}
      </DroppableInbox>
    </div>
  );
}

// --- WP FETCHER FORM ---
function FetcherForm({ onAdd }: { onAdd: (s: Story) => void }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchStories = async () => {
    setLoading(true);
    setError('');
    try {
      const apiUrl =
        `https://dallasexpress.com/wp-json/wp/v2/posts` +
        `?per_page=31&_embed&orderby=date&order=desc&_=${Date.now()}`;

      const res = await fetch(apiUrl, {
        cache: 'no-store',
      });

      if (!res.ok) throw new Error('Failed to fetch from DallasExpress.com');
      const posts = await res.json();

      [...posts].reverse().forEach((post: any) => {
        const title = decodeHtml(post.title?.rendered || '');
        const url = post.link;
        const rawDek = post.excerpt?.rendered || '';
        const dek = decodeHtml(rawDek.replace(/<[^>]+>/g, '')).trim().replace(/\[…\]/g, '...').replace(/\[\u2026\]/g, '...');
        const image = post._embedded?.['wp:featuredmedia']?.[0]?.source_url || '';
        const cats = post._embedded?.['wp:term']?.[0];
        const categoryName = cats && cats.length > 0 ? normalizeCategoryLabel(cats[0].name || '') : '';
        const category = categoryName === 'Uncategorized' ? 'NEWS' : normalizeCategoryLabel(categoryName.toUpperCase());

        onAdd({
          id: String(post.id),
          url,
          title,
          image,
          category,
          dek,
        });
      });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="text-xs text-zinc-500">Pulls the latest 30 headlines directly from dallasexpress.com API.</div>
      {error && <div className="text-xs text-red-600 bg-red-50 p-2 rounded">{error}</div>}

      <button
        onClick={fetchStories}
        disabled={loading}
        className="w-full bg-zinc-900 text-white font-medium py-2.5 rounded-lg text-sm hover:bg-black active:scale-[0.99] transition-all flex items-center justify-center gap-2 disabled:opacity-70"
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
        {loading ? 'Fetching...' : 'Load Latest Stories'}
      </button>
    </div>
  );
}

// --- MANUAL ENTRY FORM ---
function ManualForm({
  onAdd,
  storyToEdit,
  onDone,
}: {
  onAdd: (s: Story) => void;
  storyToEdit: Story | null;
  onDone: () => void;
}) {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [image, setImage] = useState('');
  const [category, setCategory] = useState('');
  const [dek, setDek] = useState('');
  const [isFetchingContent, setIsFetchingContent] = useState(false);

  useEffect(() => {
    if (storyToEdit) {
      setUrl(storyToEdit.url);
      setTitle(storyToEdit.title);
      setImage(storyToEdit.image || '');
      setCategory(storyToEdit.category || '');
      setDek(storyToEdit.dek || '');
    }
  }, [storyToEdit]);

  const handleFetchFull = async () => {
    if (!url) return;
    setIsFetchingContent(true);
    const content = await fetchArticleContent(url);
    if (content) setDek(content);
    else alert('Could not fetch full content for this URL.');
    setIsFetchingContent(false);
  };

  const handleSubmit = () => {
    if (!title) return;
    onAdd({
      id: storyToEdit ? storyToEdit.id : crypto.randomUUID(),
      url: url || '#',
      title,
      image,
      category: normalizeCategoryLabel(category),
      dek,
    });

    setUrl('');
    setTitle('');
    setImage('');
    setCategory('');
    setDek('');
    onDone();
  };

  return (
    <div className="space-y-3 animate-in slide-in-from-top-1">
      {storyToEdit && (
        <div className="text-xs font-bold text-orange-600 uppercase">
          Editing Story: {storyToEdit.title.substring(0, 20)}...
        </div>
      )}

      <input
        placeholder="Article URL"
        value={url}
        onChange={e => setUrl(e.target.value)}
        className="w-full bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:border-orange-500 outline-none"
      />
      <input
        placeholder="Headline"
        value={title}
        onChange={e => setTitle(e.target.value)}
        className="w-full bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 text-sm font-semibold focus:border-orange-500 outline-none"
      />
      <div className="grid grid-cols-2 gap-2">
        <select
          value={category}
          onChange={e => setCategory(e.target.value)}
          className="bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 text-xs focus:border-orange-500 outline-none"
        >
          <option value="">(No Tag)</option>
          <option value="CITY">CITY</option>
          <option value="NATIONAL">NATIONAL</option>
          <option value="CRIME">CRIME</option>
          <option value="BUSINESS">BUSINESS</option>
          <option value="HEALTH & SCIENCE">HEALTH & SCIENCE</option>
          <option value="POLITICS">POLITICS</option>
          <option value="PODCAST">PODCAST</option>
        </select>
        <input
          placeholder="Image URL"
          value={image}
          onChange={e => setImage(e.target.value)}
          className="bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 text-xs focus:border-orange-500 outline-none"
        />
      </div>

      <div className="relative">
        <div className="absolute top-2 right-2 z-10">
          <button
            onClick={handleFetchFull}
            disabled={!url || isFetchingContent}
            title="Fetch full article content from URL"
            className="p-1.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-600 rounded shadow-sm disabled:opacity-50"
          >
            {isFetchingContent ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          </button>
        </div>
        <textarea
          placeholder="Summary / Dek"
          value={dek}
          onChange={e => setDek(e.target.value)}
          className="w-full bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:border-orange-500 outline-none min-h-[300px] resize-y"
        />
        <div className="text-[10px] text-zinc-400 mt-1 text-right">Click icon to fetch full article body</div>
      </div>

      <button
        onClick={handleSubmit}
        className={`w-full font-medium py-2 rounded-lg text-sm active:scale-[0.99] transition-all flex items-center justify-center gap-2 ${
          storyToEdit ? 'bg-orange-600 text-white hover:bg-orange-700' : 'bg-white border border-zinc-300 text-zinc-900 hover:bg-zinc-50'
        }`}
      >
        {storyToEdit ? <Save size={16} /> : <Plus size={16} />}
        {storyToEdit ? 'Update Story' : 'Add to Inbox'}
      </button>
    </div>
  );
}

function StoryCard({
  story,
  onAssign,
  onDelete,
  onEdit,
}: {
  story: Story;
  onAssign: (b: BucketKey) => void;
  onDelete: () => void;
  onEdit: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `story:${story.id}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.6 : 1,
  } as React.CSSProperties;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className="group relative bg-white border border-zinc-200 rounded-lg p-3 hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing"
    >
      <div className="flex justify-between items-start gap-2">
        {story.image && (
          <img
            src={story.image}
            alt=""
            className="w-16 h-16 object-cover rounded-md shrink-0 border border-zinc-100"
          />
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <div className="text-xs font-bold text-orange-600 uppercase">{normalizeCategoryLabel(story.category || '')}</div>
          </div>
          <div className="text-sm font-semibold text-zinc-900 leading-tight line-clamp-2">{story.title}</div>
          <div className="text-xs text-zinc-400 mt-1 truncate">{story.url}</div>
        </div>

        <div className="flex flex-col gap-1">
          <button onClick={onEdit} className="text-zinc-300 hover:text-blue-500 p-1" type="button">
            <Edit2 size={14} />
          </button>
          <button onClick={onDelete} className="text-zinc-300 hover:text-red-500 p-1" type="button">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <div className="mt-3 pt-2 border-t border-zinc-100 flex gap-2">
        <button
          onClick={() => onAssign('top_stories')}
          className="flex-1 text-xs font-medium bg-zinc-100 hover:bg-zinc-200 text-zinc-700 py-1.5 rounded flex items-center justify-center gap-1"
          type="button"
        >
          Add to Stories
        </button>
        <button
          onClick={() => onAssign('below_weather')}
          className="flex-1 text-xs font-medium bg-zinc-100 hover:bg-zinc-200 text-zinc-700 py-1.5 rounded flex items-center justify-center gap-1"
          type="button"
        >
          Must Read
        </button>
      </div>
    </div>
  );
}

// --- DROPPABLE BUCKET WRAPPER ---
function DroppableBucket({ bucketId, children }: { bucketId: BucketKey; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: `drop:bucket:${bucketId}` });
  return (
    <div ref={setNodeRef} className={`${isOver ? 'ring-2 ring-orange-400 rounded-b-xl' : ''}`}>
      {children}
    </div>
  );
}

function BucketSection({
  config,
  slots,
  onClear,
  onMoveUp,
  onMoveDown,
  onEdit,
}: {
  config: BucketConfig;
  slots: (Story | null)[];
  onClear: (i: number) => void;
  onMoveUp: (i: number) => void;
  onMoveDown: (i: number) => void;
  onEdit: (s: Story) => void;
}) {
  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden">
      <div className="bg-zinc-50 px-4 py-3 border-b border-zinc-200 flex justify-between items-center">
        <div>
          <h3 className="font-bold text-zinc-900 text-sm uppercase">{config.label}</h3>
          <p className="text-xs text-zinc-500">{config.description}</p>
        </div>
        <div className="text-xs font-bold bg-zinc-200 px-2 py-1 rounded text-zinc-600">
          {slots.filter(Boolean).length} / {config.limit}
        </div>
      </div>

      <DroppableBucket bucketId={config.id}>
        <div className="divide-y divide-zinc-100">
          {slots.map((slot, idx) => (
            <BucketSlotRow
              key={idx}
              config={config}
              slot={slot}
              idx={idx}
              onClear={onClear}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onEdit={onEdit}
            />
          ))}
        </div>
      </DroppableBucket>
    </div>
  );
}

function BucketSlotRow({
  config,
  slot,
  idx,
  onClear,
  onMoveUp,
  onMoveDown,
  onEdit,
}: {
  config: BucketConfig;
  slot: Story | null;
  idx: number;
  onClear: (i: number) => void;
  onMoveUp: (i: number) => void;
  onMoveDown: (i: number) => void;
  onEdit: (s: Story) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `drop:slot:${config.id}:${idx}`,
  });

  return (
    <div
      ref={setNodeRef}
      className={`p-3 flex items-center gap-3 group bg-white hover:bg-zinc-50 transition-colors ${isOver ? 'bg-orange-50' : ''}`}
    >
      <div className="w-6 h-6 rounded-full bg-zinc-100 text-zinc-400 text-xs font-bold flex items-center justify-center shrink-0">
        {idx + 1}
      </div>

      <div className="flex-1 min-w-0">
        {slot ? (
          <div>
            <div className="flex items-center gap-2 mb-1">
              {config.fields.includes('category') && slot.category && (
                <span className="text-[10px] font-bold bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">
                  {normalizeCategoryLabel(slot.category || '')}
                </span>
              )}
              <span className="text-sm font-semibold text-zinc-900 truncate block">{slot.title}</span>
            </div>

            {config.fields.includes('image') && !slot.image && (
              <div className="flex items-center gap-2 text-xs text-zinc-400">
                <div className="w-2 h-2 rounded-full bg-red-300" />
                Missing Image
              </div>
            )}

            {config.fields.includes('dek') && (
              <div className="text-xs text-zinc-500 truncate mt-0.5">{slot.dek || 'No summary text...'}</div>
            )}
          </div>
        ) : (
          <div className="text-sm text-zinc-300 italic">Empty Slot (drag here or assign from inbox)</div>
        )}
      </div>

      {slot && (
        <div className="flex items-center gap-1 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => onEdit(slot)}
            className="p-1.5 hover:bg-white hover:shadow rounded text-zinc-500 hover:text-blue-600"
            type="button"
          >
            <Edit2 size={14} />
          </button>
          <button
            onClick={() => onMoveUp(idx)}
            disabled={idx === 0}
            className="p-1.5 hover:bg-white hover:shadow rounded text-zinc-500 hover:text-zinc-900 disabled:opacity-20"
            type="button"
          >
            <ArrowUp size={14} />
          </button>
          <button
            onClick={() => onMoveDown(idx)}
            disabled={idx === config.limit - 1}
            className="p-1.5 hover:bg-white hover:shadow rounded text-zinc-500 hover:text-zinc-900 disabled:opacity-20"
            type="button"
          >
            <ArrowDown size={14} />
          </button>
          <button
            onClick={() => onClear(idx)}
            className="p-1.5 hover:bg-red-50 hover:text-red-600 rounded text-zinc-400 ml-1"
            type="button"
          >
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
